import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from '@untitledui/icons';
import { ROUTES } from '@/shared/config/constants';
import { KnowMatchLogo } from '@/shared/ui';
import { clearSignupDraft } from './useSignupForm';
import { ExitConfirmModal } from './ExitConfirmModal';
import { SignupProgressBar } from './SignupProgressBar';

type SignupLayoutProps = {
  currentStepIndex: number;
  totalSteps: number;
  title: string;
  subtitle?: string;
  onPrev?: () => void;
  canGoPrev: boolean;
  children: ReactNode;
  footer: ReactNode;
};

export const SignupLayout = ({
  currentStepIndex,
  totalSteps,
  title,
  subtitle,
  onPrev,
  canGoPrev,
  children,
  footer,
}: SignupLayoutProps) => {
  const navigate = useNavigate();
  const [exitModalOpen, setExitModalOpen] = useState(false);

  const handleConfirmExit = () => {
    clearSignupDraft();
    setExitModalOpen(false);
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <main className="relative flex min-h-dvh flex-col bg-white">
      <SignupProgressBar currentStepIndex={currentStepIndex} totalSteps={totalSteps} />

      <header className="relative flex items-center justify-center px-6 pb-2 pt-10 sm:pt-12">
        {canGoPrev && onPrev ? (
          <button
            type="button"
            onClick={onPrev}
            className="absolute left-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 sm:left-6"
            aria-label="Etapa anterior"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        ) : null}
        <KnowMatchLogo
          className="h-[35px] w-auto max-w-full text-gray-900"
          aria-label="Know-How Match"
        />
        <button
          type="button"
          onClick={() => setExitModalOpen(true)}
          className="absolute right-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 sm:right-6"
          aria-label="Sair do cadastro"
        >
          <X className="h-[22px] w-[22px]" strokeWidth={2.2} />
        </button>
      </header>

      <section className="flex flex-1 items-center justify-center px-6 pb-32 pt-6 sm:pb-10 sm:pt-10">
        <div className="w-full max-w-[640px]">
          <p className="text-sm font-medium text-gray-500">
            {currentStepIndex + 1} de {totalSteps}
          </p>
          <h1 className="mt-2 text-[28px] font-semibold leading-tight tracking-tight text-gray-900 md:text-[36px]">
            {title}
          </h1>
          {subtitle ? (
            <p className="mt-3 text-base leading-relaxed text-gray-500">{subtitle}</p>
          ) : null}

          <div className="mt-8 sm:mt-10">{children}</div>

          <div className="mt-8 hidden sm:block">{footer}</div>

          <p className="mt-6 text-center text-sm text-gray-500 sm:mt-8">
            Já tem uma conta?{' '}
            <Link
              to={ROUTES.LOGIN}
              className="font-semibold text-gray-900 underline-offset-4 hover:underline"
            >
              Fazer login
            </Link>
          </p>
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-100 bg-white px-6 pb-[calc(env(safe-area-inset-bottom)+16px)] pt-4 sm:hidden">
        {footer}
      </div>

      <ExitConfirmModal
        open={exitModalOpen}
        onClose={() => setExitModalOpen(false)}
        onConfirm={handleConfirmExit}
      />
    </main>
  );
};
